// Обработка SSO-хэндоффа из ЛК лендинга: URL вида /sso?nonce=...
// Обмениваем nonce на JWT-пару, сохраняем сессию и сразу перекидываем на /.
// Если nonce невалидный/просрочен — показываем мягкую ошибку и кнопку «Войти»,
// чтобы владелец не залип на белом экране.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

import { ssoExchange } from '@/services/auth';
import { useAuthStore } from '@/store/auth.store';
import { useEstablishmentStore } from '@/store/establishment.store';
import logoWide from '@/images/Resstologo2.png';

export default function SsoHandoff() {
  const [params] = useSearchParams();
  const nonce = params.get('nonce');
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const setEstablishmentId = useEstablishmentStore((s) => s.setCurrentId);

  // React 18 StrictMode в dev удваивает эффекты — без guard'а мы бы сжигали
  // nonce два раза и получали 401 на втором запросе (nonce one-shot).
  const exchangingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nonce) {
      setError('Ссылка входа недействительна. Войдите в ЛК ещё раз.');
      return;
    }
    if (exchangingRef.current) return;
    exchangingRef.current = true;

    (async () => {
      try {
        const res = await ssoExchange(nonce);
        setSession({
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          user: res.user,
        });
        setEstablishmentId(res.user.establishmentId ?? null);
        toast.success(`Добро пожаловать${res.user.name ? ', ' + res.user.name : ''}`);
        navigate('/', { replace: true });
      } catch {
        // Тост с конкретной ошибкой уже показал axios-интерсептор.
        setError('Не удалось войти автоматически. Попробуйте ещё раз из ЛК.');
      }
    })();
  }, [nonce, navigate, setSession, setEstablishmentId]);

  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-md card p-7 text-center">
        <img src={logoWide} alt="Resstro" className="h-14 w-auto object-contain mx-auto mb-4" />
        {!error ? (
          <div className="flex items-center justify-center gap-2 text-slate-600">
            <Loader2 size={18} className="animate-spin" />
            <span>Входим в админ-панель…</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-amber-600">
              <AlertTriangle size={18} />
              <span className="text-sm">{error}</span>
            </div>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => navigate('/login', { replace: true })}
            >
              Войти вручную
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
